import type { ChatMessage } from './types';
import { PENDING_SOURCE_TAGS } from './interruptedTurn';
import { deriveTaskAnchorId } from '../../types/taskAnchor';

type ThreadMessagesReader = (threadId: string) => ChatMessage[] | null | undefined;
type ThreadMessagesWriter = (threadId: string, messages: ChatMessage[]) => void;

type FinalizePersistTarget =
  | { index: number; id?: never }
  | { id: string; index?: never };

function normalizeFinalizedSourceTag(
  nextSourceTag: string | undefined,
  previousSourceTag: string | undefined,
): string | undefined {
  const candidate = nextSourceTag ?? previousSourceTag;
  if (!candidate) return undefined;
  return PENDING_SOURCE_TAGS.has(candidate) ? undefined : candidate;
}

function buildFinalizedPersistedMessage(
  previous: ChatMessage | undefined,
  final: Partial<ChatMessage>,
  text: string | undefined,
  html: string | undefined,
): ChatMessage {
  const normalizedSourceTag = normalizeFinalizedSourceTag(final.sourceTag, previous?.sourceTag);
  const next: ChatMessage = {
    ...(previous || {
      text: '',
      isUser: false,
      timestamp: new Date(),
    }),
    // Spread `final` first so any stale interrupted fields can be normalized
    // below instead of accidentally surviving a partial completion update.
    ...final,
    text: text ?? final.text ?? previous?.text ?? '',
    html: html ?? final.html ?? previous?.html,
    status: final.status ?? 'complete',
    interrupted: false,
  };

  // CONVERGENCE ANCHOR freeze (Finding 2): capture the durable per-doc key from
  // serverTaskId BEFORE it is deleted below. This is the finalize boundary for
  // BOTH active and background threads (the latter bypasses the reducer
  // normalizer), so it guarantees a finalized task answer keeps its anchor and
  // the client's persisted copy converges with the server's on ONE doc.
  if (!next.taskAnchorId) {
    const anchor = deriveTaskAnchorId(next.serverTaskId ?? previous?.serverTaskId);
    if (anchor) next.taskAnchorId = anchor;
  }

  // If the message had been marked interrupted during a hydrate/resume path,
  // completion must clear those fields so the renderer no longer treats the
  // bubble as recoverable/interrupted.
  delete (next as any).regeneratePrompt;
  delete (next as any).errorInfo;
  delete (next as any).serverTaskId;

  if (normalizedSourceTag) {
    next.sourceTag = normalizedSourceTag;
  } else {
    delete next.sourceTag;
  }
  return next;
}

export function persistFinalizedStreamingMessageToThreadStorage(args: {
  originatingThreadId: string | null;
  readThreadMessages?: ThreadMessagesReader | null;
  writeThreadMessages?: ThreadMessagesWriter | null;
  target: FinalizePersistTarget;
  final: Partial<ChatMessage>;
  text?: string;
  html?: string;
}): void {
  const {
    originatingThreadId,
    readThreadMessages,
    writeThreadMessages,
    target,
    final,
    text,
    html,
  } = args;

  if (!originatingThreadId || !readThreadMessages || !writeThreadMessages) return;

  const fresh = readThreadMessages(originatingThreadId) ?? [];
  if ('id' in target) {
    const idx = fresh.findIndex((msg) => msg?.id === target.id);
    if (idx >= 0) {
      const next = fresh.slice();
      next[idx] = buildFinalizedPersistedMessage(next[idx], final, text, html);
      writeThreadMessages(originatingThreadId, next);
      return;
    }
    // T3 LOG-ON-FIRE (2026-08-22): appending a finalized answer into a
    // transcript that has NO trace of its placeholder is the exact write
    // that put "the calendar answer in the email thread" when an origin was
    // mis-derived upstream. The append stays (losing a finished answer is
    // strictly worse) but it must never be silent — this breadcrumb is the
    // production attribution for any residual wrong-origin caller.
    console.warn('[streamFinalizePersist] append-on-miss: finalized message had no placeholder in its claimed thread', {
      threadId: originatingThreadId,
      id: target.id,
      sourceTag: (final as any)?.sourceTag || null,
    });
    writeThreadMessages(originatingThreadId, [
      ...fresh,
      buildFinalizedPersistedMessage(undefined, { ...final, id: final.id || target.id }, text, html),
    ]);
    return;
  }

  if (target.index >= 0 && target.index < fresh.length) {
    const next = fresh.slice();
    next[target.index] = buildFinalizedPersistedMessage(next[target.index], final, text, html);
    writeThreadMessages(originatingThreadId, next);
    return;
  }

  console.warn('[streamFinalizePersist] append-on-miss (index): finalized message fell past its claimed thread transcript', {
    threadId: originatingThreadId,
    index: target.index,
    sourceTag: (final as any)?.sourceTag || null,
  });
  writeThreadMessages(originatingThreadId, [
    ...fresh,
    buildFinalizedPersistedMessage(undefined, final, text, html),
  ]);
}
